import isUndefined from 'lodash/isUndefined';
import cloneDeep from 'lodash/cloneDeep';
import qs from 'qs';
import { isNullOrUndefined } from '../predicates/type';
import { CustomObject } from '../types';
import stringUtils from './string';

export const removeQueryParam = (sourceURL: string, key: string) => {
	let rtn = sourceURL.split('?')[0];
	let param;
	let paramsArr: string[] = [];

	const hasQueryPrefix = sourceURL.indexOf('?') !== -1;
	const queryString = hasQueryPrefix ? sourceURL.split('?')[1] : '';

	if (queryString.trim() !== '') {
		paramsArr = queryString.split('&');

		for (let i = paramsArr.length - 1; i >= 0; i -= 1) {
			param = paramsArr[i].split('=')[0];
			if (param.toLowerCase() === key.toLowerCase()) {
				paramsArr.splice(i, 1);
			}
		}

		if (paramsArr.length > 0) {
			rtn = `${rtn}?${paramsArr.join('&')}`;
		}
	}

	return rtn;
};

export const addQueryParam = (uri, key, value) => {
	uri = uri || '';

	if (
		stringUtils.isNullOrEmpty(key) ||
		(stringUtils.isNullOrEmpty(uri.trim()) && stringUtils.isNullOrEmpty(key.trim()))
	) {
		return '';
	}

	if (stringUtils.isNullOrEmpty(value)) {
		value = '';
	}

	const i = uri.indexOf('#');
	const hash = i === -1 ? '' : uri.substr(i);
	uri = i === -1 ? uri : uri.substr(0, i);

	const re = new RegExp(`([?&])${key}=.*?(&|$)`, 'i');
	const separator = uri.indexOf('?') !== -1 ? '&' : '?';
	if (uri.match(re)) {
		uri = uri.replace(re, `$1${key}=${value}$2`);
	} else {
		uri = `${uri}${separator}${key}=${value}`;
	}

	return uri + hash;
};

export const removeQueryParamAndNavigate = (key) => {
	window.history.replaceState({}, document.title, removeQueryParam(window.location.href, key));
};

export const getPagePathId = (): string => {
	const url = new URL(window.location.href);

	const id = url.pathname.split('/').pop();

	if (isNullOrUndefined(id)) {
		throw new Error('No page path id found');
	}

	return id;
};

/**
 * @param {string} search
 * @param {Object} params
 * @param {boolean} [params.toLowerCase=false]
 * @param {boolean} [params.useHash=false]
 */
export function splitQuerySearchParams(search, { toLowerCase = false, useHash = false } = {}) {
	const urlSearch = new URLSearchParams(search);
	const params = {};

	for (const value of urlSearch.entries()) {
		let key = value[0];

		if (useHash) {
			key = key.replace('#', '');
		}
		if (toLowerCase) {
			params[String(key).toLowerCase()] = value[1];
		} else {
			params[key] = value[1];
		}
	}

	return params;
}

type GetUrlParamsOptions = {
	toLowerCase?: boolean;
	useHash?: boolean;
};

/**
 * @param {string} url
 * @param {Object} params
 * @param {boolean} [params.toLowerCase=false]
 * @param {boolean} [params.useHash=false]
 */
export const getUrlParams = (url, { toLowerCase = false, useHash = false }: GetUrlParamsOptions = {}) => {
	const urlElement = new URL(url);
	const urlQuery = useHash ? urlElement.hash : urlElement.search;
	const urlSearch = new URLSearchParams(urlQuery);

	return splitQuerySearchParams(urlSearch, {
		toLowerCase,
		useHash,
	});
};

export const getWindowUrlParams = (params: GetUrlParamsOptions = {}) => {
	//eslint-disable-next-line
	return getUrlParams(window.location.href, params) as any;
};

export function getWindowParsedQueryParams() {
	const search = window.location.search.replace('?', '');

	return qs.parse(search, {
		comma: true,
		allowDots: true,
	});
}

export function filterParsedQueryParams(
	queryParams: CustomObject,
	{
		skip,
	}: {
		skip?: string[];
	},
) {
	const newQueryParams: CustomObject = cloneDeep(queryParams);
	const newQueryParamsKeys = Object.keys(newQueryParams).map((i) => ({
		key: i,
		normalizedKey: String(i).toLowerCase(),
	}));

	skip.forEach((key) => {
		const normalizedKey = String(key).toLowerCase();
		const target = newQueryParamsKeys.find((i) => i.normalizedKey === normalizedKey);

		if (!isUndefined(target)) {
			delete newQueryParams[target.key];
		}
	});

	return newQueryParams;
}

export function getHrefPathName(href: string) {
	const nextUrl = new URL(String(href).toLowerCase(), window.location.origin);
	return nextUrl.pathname;
}
